import type { RawDraftContentState } from 'draft-js'
import { enumNarrow } from '../helpers/enumGuard'
import type { IReactionSummary } from '../helpers/reactions'
import { ActivityStatus, ActivityType } from './activity'
import { OngoingReportingStatus } from './bbc'
import { type BoxSignStatusLabel, MANUALLY_SEND_LABEL, type IFile } from './box'
import type { IClientInfo } from './client'
import type {
  ICustomFieldOption,
  ITaskCustomFieldEntry,
  ITaskCustomFieldValueData,
} from './customField'
import type { ITaskFeedback } from './feedback'
import { AmendmentAnalysisStatus } from './generalAnalysis'
import type { ITaskProjectPlacement } from './taskProject'
import type { UserRole } from './user'

// Statuses the task system owns: the ad-hoc lifecycle + the reserved terminal
// words sources normalize onto (Complete/Archived) + doc-review's InReview.
// Source statuses (activity / reporting / box-sign) and the funding-send labels
// are NOT mirrored here — a task can hold any of those too (see TaskStatusValue);
// a new status on a source enum needs no change here.
//
// Values are string literals (not ActivityStatus.NotStarted-style enum-to-enum
// references) on purpose — under Vite's isolatedModules/esbuild transform, a
// cross-file enum reference can't be resolved as a constant expression, so
// esbuild falls back to emitting a reverse-mapped member. That silently adds
// the bare key ("NotStarted") as an extra enumerable value alongside the
// intended "Not Started", which doubled every option in the Tasks status
// filter (Object.values(TaskStatus) in TASK_STATUS_OPTIONS). Keep these in
// sync by value with ActivityStatus/OngoingReportingStatus, not by reference.
export enum TaskStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  Blocked = 'Blocked',
  InReview = 'In Review',
  Complete = 'Complete',
  Archived = 'Archived',
}

// A task is "closed" once it reaches a terminal status. Shared across the tasks
// domain so the closed-status set is defined in exactly one place.
export const CLOSED_TASK_STATUSES = [TaskStatus.Complete, TaskStatus.Archived] as const

export const isClosedTaskStatus = (status: string | null | undefined): boolean =>
  (CLOSED_TASK_STATUSES as readonly (string | null | undefined)[]).includes(status)

/** The statuses a person can set on an ad-hoc task (and a rule can watch or set). */
export const AD_HOC_TASK_STATUSES = [
  TaskStatus.NotStarted,
  TaskStatus.InProgress,
  TaskStatus.Blocked,
  TaskStatus.Complete,
] as const

// Every value `task.status` may hold: task-owned ∪ each source workflow's own
// status enum ∪ the funding manual-send label. Derived from those types, so a
// new source status flows in automatically with nothing to mirror by hand.
export type TaskStatusValue =
  | TaskStatus
  | ActivityStatus
  | OngoingReportingStatus
  | BoxSignStatusLabel
  | AmendmentAnalysisStatus
  | typeof MANUALLY_SEND_LABEL

export enum TaskCategory {
  Funding = 'Funding',
  Collections = 'Collections',
  Reporting = 'Reporting',
  AdHoc = 'Ad-Hoc',
  FlagsInvestigation = 'Flags Investigation',
  BDDocuments = 'BD Documents',
  DDDocuments = 'DD Documents',
  CommitteeSubmission = 'Committee Submission',
  Amendments = 'Amendments',
  TermsApproval = 'Terms Approval',
}

/** Cast-free narrower for TaskCategory (see common/helpers/enumGuard). */
export const asTaskCategory = enumNarrow(TaskCategory).as

// What CREATED a task — mutually exclusive by construction, stamped on the row
// by whichever path created it and never rewritten afterwards. The nine
// workflow values are string-identical to their TaskCategory counterparts so the
// two stay comparable while Category is migrated out; the last three answer the
// question Category never could ("a human typed it" vs Arc vs a call transcript).
//
// Recurrence is deliberately NOT a value here: any task can be made recurring
// (the affordance is gated on internal-user, not on task type), so it coexists
// with every source rather than replacing one. `recurrenceRuleId` carries it.
export enum TaskSource {
  Funding = TaskCategory.Funding,
  Collections = TaskCategory.Collections,
  Reporting = TaskCategory.Reporting,
  FlagsInvestigation = TaskCategory.FlagsInvestigation,
  BDDocuments = TaskCategory.BDDocuments,
  DDDocuments = TaskCategory.DDDocuments,
  CommitteeSubmission = TaskCategory.CommitteeSubmission,
  Amendments = TaskCategory.Amendments,
  TermsApproval = TaskCategory.TermsApproval,
  Manual = 'Manual',
  Arc = 'Arc / Automation',
  Call = 'Call',
  // Captured from an AIM message. Nothing stamps this yet — it exists so AIM's
  // message-capture path has a source to write, instead of landing as Manual and
  // losing the link back to the conversation. "AIM" matches the product's own
  // naming; the value is what's stored AND what the Source filter shows.
  Message = 'AIM Message',
  // Filed from a feedback submission onto the Atlas Product Board's Inbound section.
  Feedback = 'Feedback',
}

/** Cast-free narrower for TaskSource (see common/helpers/enumGuard). */
export const asTaskSource = enumNarrow(TaskSource).as

// Sources Atlas creates tasks from automatically.
const AUTO_TASK_SOURCES = new Set<string>([
  TaskSource.Funding,
  TaskSource.Collections,
  TaskSource.Reporting,
  TaskSource.FlagsInvestigation,
  TaskSource.BDDocuments,
  TaskSource.DDDocuments,
  TaskSource.Amendments,
  TaskSource.Feedback,
])

/** Membership test over a free-string column, cast-free (see enumGuard's note). */
export const isAutoTaskSource = (source: string | null | undefined): boolean =>
  source != null && AUTO_TASK_SOURCES.has(source)

type ILinkedTaskFields = {
  source: string | null | undefined
  activityId: string | null | undefined
  ongoingReportingId: string | null | undefined
  flagId: string | null | undefined
  opsReportingId: string | null | undefined
  generalAnalysisId: string | null | undefined
}

/**
 * The task's upstream record still exists, so its status, company and category
 * mirror that record. Every task FK is SET NULL, so deleting the record frees the
 * task. DD tasks carry no FK (their record is the client's open DD requests), and
 * BD shares `opsReportingId` with the editable Committee Submission / Terms tasks.
 */
export const isLinkedTask = (task: ILinkedTaskFields): boolean =>
  !!(task.activityId || task.ongoingReportingId || task.flagId || task.generalAnalysisId) ||
  task.source === TaskSource.DDDocuments ||
  (task.source === TaskSource.BDDocuments && !!task.opsReportingId)

/** The linked record hasn't finished. `workflowStatus` keeps its status once
 *  `status` belongs to the user. */
export const hasOpenLinkedRecord = (
  task: ILinkedTaskFields & { status: string | null; workflowStatus?: string | null },
): boolean => {
  const workflowStatus = task.workflowStatus ?? task.status
  return isLinkedTask(task) && !isClosedTaskStatus(workflowStatus)
}

/**
 * An Amendments task mirrors its amendment analysis: its status is the
 * analysis's status, so a status change made on the task is routed through the
 * analysis (`generalAnalysis/UpdateStatus`) rather than written on the task.
 */
export const isAmendmentTask = (task: {
  source: string | null | undefined
  generalAnalysisId: string | null | undefined
}): boolean => task.source === TaskSource.Amendments && !!task.generalAnalysisId

// How a (task, user) collaborator relationship formed. Auto sources are added
// by the task write paths (commenting, being @-mentioned, being/having been the
// assignee, creating the task); Manual is an explicit add on the task detail.
// Persisted for display + so auto-cleanup never strips a manually-added row.
export enum TaskCollaboratorSource {
  Creator = 'Creator',
  Assignee = 'Assignee',
  Comment = 'Comment',
  Mention = 'Mention',
  Manual = 'Manual',
}

// How a recurring-task rule handles a new occurrence when the previous one is
// still open. Skip (default) leaves the existing task untouched so it stays
// overdue; CreateNew always spawns a fresh occurrence.
export enum TaskRecurrenceOnOpenBehavior {
  Skip = 'Skip',
  CreateNew = 'CreateNew',
}

export enum TaskRecurrenceFrequency {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
  // N days after the previous occurrence is completed.
  Periodically = 'Periodically',
  // Every N days, weeks or months.
  Custom = 'Custom',
}

export enum TaskRecurrenceMonthlyMode {
  DayOfMonth = 'DayOfMonth',
  LastDay = 'LastDay',
  // Last weekday of the month; bank holidays are not considered.
  LastBusinessDay = 'LastBusinessDay',
}

export enum TaskRecurrenceIntervalUnit {
  Days = 'Days',
  Weeks = 'Weeks',
  Months = 'Months',
}

/** When a recurring task repeats, evaluated in America/New_York. */
export interface ITaskRecurrenceSchedule {
  frequency: TaskRecurrenceFrequency
  // 'HH:mm', 24-hour.
  time: string
  // Weekly, and Custom in weeks: 0=Sun..6=Sat.
  daysOfWeek?: number[]
  // Monthly, and Custom in months.
  monthlyMode?: TaskRecurrenceMonthlyMode
  dayOfMonth?: number
  // Custom (with intervalUnit) and Periodically (always days).
  interval?: number
  intervalUnit?: TaskRecurrenceIntervalUnit
}

/** File row attached to activity / BBC / reporting on task show payload */
export type ITaskLinkedFile = Pick<
  IFile,
  'id' | 'name' | 'fileName' | 'boxLink' | 'link' | 'size' | 'fileId'
>

/** Lightweight user shape embedded on task / comment / activity-log payloads.
 *  Only the identity fields the UI needs for an avatar + name/email display —
 *  not the full IUser. Avatar is optional because not every embedding source
 *  populates it. */
export interface ITaskEmbeddedUser {
  id: string
  /**
   * Self-set display name, preferred over first/last wherever this user is
   * rendered — see `formatAssigneeLabel` / `formatUserFullName`. Optional
   * because most embedded payloads predate it and absent means "use the legal
   * name".
   */
  nickname?: string | null
  firstName: string
  lastName: string
  email: string
  avatar?: string
  // Fallback photo for users whose avatar comes from Google (no uploaded one).
  // Lets raw-MUI-Avatar surfaces (collaborator stack/chips) match CommonAvatar.
  googleAvatarUrl?: string | null
  // Lets the UI detect an Arc/bot-authored comment (role === BOT_USER), same
  // check Note.tsx already does off the full IUser it embeds for notes.
  role?: UserRole
}

/** BorrowingBase summary nested inside `ITaskActivitySummary` on a task payload. */
export interface ITaskActivityBorrowingBase {
  id: string
  files?: IFile[] | null
}

/** Activity record summary embedded on a structured task payload. */
export interface ITaskActivitySummary {
  id: string
  type: ActivityType
  status: string
  recordDate: string
  clientName: string | null
  borrowingBaseId: string | null
  clientInfo?: IClientInfo | null
  files?: ITaskLinkedFile[] | null
  borrowingBase?: ITaskActivityBorrowingBase | null
}

/**
 * The company a task is linked to, as projected onto a task payload. Carries the
 * lifecycle fields (not just the name) so the Company link can resolve to the
 * company's page *at its current stage* without a second request — client page,
 * diligence, prospect overview, or the CRM company page for a lead / opportunity.
 */
export interface ITaskCompany {
  id: string
  name: string | null
  clientInfoId: string | null
  clientStatus: string | null
  firstOPSReportingId: string | null
}

/** The Company / Org column's fallback for a task with no company of its own: its
 *  organization, else its person's primary company or organization. Exactly one of
 *  `company` (routed by stage, like ITask.company) and `organizationId` is set. */
export interface ITaskAffiliation {
  name: string
  company: ITaskCompany | null
  organizationId: string | null
}

/** Fireflies transcript summary embedded on a Call task payload — just enough
 *  for the source indicator to link out to the recording. `id` is carried
 *  because the join needs the key to hydrate the relation, not for display. */
export interface ITaskFirefliesTranscriptSummary {
  id: string
  transcriptUrl: string | null
}

/** What produced an Arc-created task, resolved from the chat session the
 *  creating service call came from (`arcSessionId`).
 *
 *  `automation` is a scheduled or event-triggered run and names the automation;
 *  `chat` is a person asking Arc directly, which has nothing to open — the
 *  indicator states who asked and when instead. Null on tasks created before the
 *  session was recorded, which read as neither.
 *
 *  `canOpen` is per-viewer: any internal user can see a task, but
 *  automations/Get scopes a non-admin to their own automations, so a link for
 *  someone else's would 404. False means name it without linking. */
export type ITaskArcOrigin =
  | {
      kind: 'automation'
      automationId: string
      automationName: string
      automationOwnerName: string | null
      canOpen: boolean
      // The human this origin resolves to — the automation's owner here, the
      // asker for a chat origin. Carried so the delete verdict doesn't re-query
      // the session Show has already looked up (taskDeletePermission).
      requesterUserId: string | null
    }
  | { kind: 'chat'; userName: string | null; requesterUserId: string | null }

export interface ITaskFeedbackOrigin {
  submitterName: string | null
  submittedAt: string
  // Internal users can open the submission; an external viewer can't read feedback.
  canOpen: boolean
}

/** OngoingReporting record summary embedded on a structured task payload. */
export interface ITaskOngoingReportingSummary {
  id: string
  type: string
  status: string
  clientName: string
  clientInfo?: IClientInfo | null
  files?: IFile[] | null
}

/** A user following a task (Asana-style). Includes the creator, current/past
 *  assignees, comment authors, @-mentioned users, and manually-added users. Grants
 *  no access — internal users can already see every task — it's a follow/notify
 *  relationship only. `user` is embedded for the detail panel's avatar + name. */
export interface ITaskCollaborator {
  id: string
  taskId: string
  userId: string
  source: TaskCollaboratorSource
  user?: ITaskEmbeddedUser | null
  createdAt: string
  updatedAt: string
}

export interface ITask {
  id: string
  title: string
  // Structured rich text (Draft.js). Null for legacy/auto-generated tasks that
  // only have plain text — read `descriptionText` for a plain-text view.
  description: RawDraftContentState | null
  descriptionText: string | null
  status: TaskStatusValue | null
  dueDate: string | null
  completedAt: string | null
  assignedToUserId: string | null
  assignedToUser?: ITaskEmbeddedUser | null
  createdByUserId: string | null
  createdByUser?: ITaskEmbeddedUser | null
  /** Set when a personal access token created it; the client shows an Automated chip. */
  accessTokenId?: string | null
  activityId: string | null
  activity?: ITaskActivitySummary | null
  ongoingReportingId: string | null
  ongoingReporting?: ITaskOngoingReportingSummary | null
  flagId: string | null
  opsReportingId: string | null
  // Set on Amendments-category tasks — links the task to its amendment
  // analysis so the category cell can deep-link to the analysis page.
  generalAnalysisId: string | null
  // Auto-created tasks: fields a person has overridden, which the sync then
  // leaves alone. `workflowStatus` is the linked record's own status.
  isTitleCustom?: boolean
  isStatusCustom?: boolean
  isDueDateCustom?: boolean
  workflowStatus?: TaskStatusValue | null
  parentTaskId: string | null
  // `placements` rides only on the detail payload (Show), so a subtask can follow its parent's boards.
  parentTask?: (Pick<ITask, 'id' | 'title'> & Pick<Partial<ITask>, 'placements'>) | null
  // Manual sort order among siblings under the same parent (null on top-level
  // tasks, which order by their placement position).
  subtaskPosition?: number | null
  subtasks?: ITask[]
  // List payload only: counts so a row can show the expand arrow + completion
  // warning without the subtask rows loaded (those are fetched lazily on expand).
  subtaskCount?: number
  incompleteSubtaskCount?: number
  // My Tasks list payload only: which of the VIEWER'S OWN sections this task is
  // filed under, and where in it. Null means No Section — nothing files itself,
  // so a task is in a section only because the viewer dragged it there. Entirely
  // separate from the task's section on any project board.
  myTasksSectionId?: string | null
  myTasksPosition?: number | null
  // Show payload only: whether the viewer may delete this task, and the reason
  // they may not (creator/board-admin rule, or the task being kept in sync with
  // another workflow). The panel gates the Delete Task item on these rather than
  // reimplementing the rule — see server/src/services/tasks/taskDeletePermission.ts.
  canDelete?: boolean
  deleteBlockedReason?: string | null
  // Per-reader: the current user has never opened this task's detail panel. Only
  // ever true for tasks assigned to them, so the row dots and the nav badge count
  // the same set (see server/src/services/tasks/unreadScope.ts).
  isUnread?: boolean
  clientInfo?: IClientInfo | null
  clientName: string | null
  // The task's category, as the selected option of the global Category custom
  // field. Carries the option's colour so a row renders the pill without a
  // second lookup. Populated by the list/show endpoints; null when unset.
  categoryOption?: ICustomFieldOption | null
  // Stamped by the creating path; system-owned, never accepted from a client.
  source: TaskSource
  // The bot working this task (Arc or Dwight Bot) and its name. Flipped live over
  // the ATLAS_AI_TASK_WORKING websocket event, so the row and the panel can show a
  // working line (and, for Arc, a Stop control) without waiting for a refetch.
  workingUserId?: string | null
  workingUserName?: string
  // Whether the viewer may stop that run (assignee or collaborator). Present
  // only on tasks Arc is working; the cancel service re-checks server-side.
  canStopArc?: boolean
  // Who may stop it, carried by the ATLAS_AI_TASK_WORKING event and patched onto
  // the task by the reducer. `canStopArc` is resolved per read, so it is absent
  // on a flag that flipped live — these ids are what keep Stop reachable before
  // the next fetch (see canStopArcRun on the client).
  arcStoppableByUserIds?: string[]
  // Call tasks: the transcript the task came out of, and the offset in seconds
  // of the sentence the source link jumps to (resolved on read from the task's
  // cited sentence index). Null offset means open the transcript at the top.
  firefliesTranscript?: ITaskFirefliesTranscriptSummary | null
  callStartTime?: number | null
  // Arc tasks: the automation or chat behind the task, resolved from the chat
  // session that created it. Null when that session was never recorded.
  arcOrigin?: ITaskArcOrigin | null
  // Feedback tasks: the submission the task was filed from. `feedback` is Show-only.
  feedbackSubmissionId?: string | null
  feedback?: ITaskFeedback | null
  // Feedback tasks: who submitted it, for the source indicator.
  feedbackOrigin?: ITaskFeedbackOrigin | null
  // When Arc's triage pass on this task started, while one is running; null otherwise.
  triageStartedAt?: string | null
  // CRM person this to-do is attached to (person-profile running agenda).
  personId?: string | null
  // The company this task is linked to. `companyId` is the canonical link;
  // `clientName` above is its mirror, present only when the company is a client.
  // Display prefers company.name and falls back to clientName (structured tasks
  // carry no companyId).
  companyId?: string | null
  company?: ITaskCompany | null
  affiliation?: ITaskAffiliation | null
  // Files attached directly to this task (My Tasks attachments). Populated by
  // the Show endpoint; not present on list payloads.
  files?: IFile[] | null
  // Box folder id for this task's attachments, set on first upload.
  boxFolderId?: string | null
  // Set when this task was spawned by (or converted into the seed of) a
  // recurring-task rule. Drives the row recurrence icon + skip-while-open lookup.
  recurrenceRuleId: string | null
  recurrenceRule?: ITaskRecurrenceRule | null
  // Project/section placements (multi-homing). Populated by Show and the
  // per-project list in full; the My Tasks list carries a lighter form for the
  // Projects column's pills — project id/name/colour, no section relation, and
  // narrowed to the boards the viewer is a member of (see attachTaskProjects).
  placements?: ITaskProjectPlacement[]
  // Custom-field entries (applicable field + current value), resolved from the
  // task's project attachments. Populated by Show.
  customFields?: ITaskCustomFieldEntry[]
  // The per-project list's values only, keyed by field id; the fields themselves
  // come once on the response, not on every row. Unset fields are absent.
  customFieldValues?: Record<string, ITaskCustomFieldValueData>
  // Users following this task (creator, current/past assignees, commenters,
  // @-mentioned, manually added). Populated by Show; not on list payloads.
  collaborators?: ITaskCollaborator[]
  // Files attached to this task's comments, so the Files section can list every
  // file on the task. Populated by Show; read-only there — a comment's file is
  // removed by editing or deleting that comment.
  commentAttachments?: ITaskCommentAttachment[]
  createdAt: string
  updatedAt: string
}

/**
 * Minimal task payload behind a rendered task deep-link (the smart-link chip).
 * Deliberately tiny: a note can hold several links, and the chip only needs the
 * title plus enough source ids to pick a status icon — not the relation trees
 * and inline-image data URLs that `Show` returns.
 */
// `source` earns its place alongside the FKs: a DD Documents task carries no FK at
// all, so without it `isAdHocTask` would read every such task as ad-hoc and the chip
// would show an incomplete checkbox where a structured glyph belongs.
export type ITaskLinkPreview = Pick<ITask, 'id' | 'title' | 'status'>

/** Recurring-task rule that spawns ad-hoc task occurrences on a schedule. */
export interface ITaskRecurrenceRule {
  id: string
  userId: string
  title: string
  description: RawDraftContentState | null
  descriptionText: string | null
  category: TaskCategory | null
  // The company each spawned task copies. `clientName` is its mirror, exactly as
  // a task holds the pair (see ITask.companyId).
  clientName: string | null
  companyId: string | null
  // Resolved name for `companyId`, projected by the Get endpoint so the edit
  // modal's name-keyed picker can show the current company for a lead-linked rule
  // (which has no clientName to fall back on).
  companyName?: string | null
  schedule: ITaskRecurrenceSchedule
  // 'YYYY-MM-DD'; no occurrence is scheduled before it.
  startDate: string | null
  onOpenBehavior: TaskRecurrenceOnOpenBehavior
  isActive: boolean
  nextRunAt: string | null
  lastRunAt: string | null
  createdAt: string
  updatedAt: string
}

// Partial payload for an inline task edit (one or more cells changed on a row).
export interface IInlineTaskUpdate {
  title?: string
  status?: TaskStatusValue
  dueDate?: string | null
  assignedToUserId?: string | null
  companyId?: string | null
}

export interface ITaskComment {
  id: string
  taskId: string
  userId: string
  user?: ITaskEmbeddedUser
  /** Set when a personal access token posted it; the client shows an Automated chip. */
  accessTokenId?: string | null
  // Draft.js rich content (mentions + inline images), the source of truth.
  // Mirrors ITask.description. Null for legacy comments.
  content?: RawDraftContentState | null
  // Plain-text mirror of content (or the original body for legacy comments).
  // Mirrors ITask.descriptionText; renderers fall back to it when content null.
  contentText?: string | null
  mentionedUserIds: string[] | null
  // Null means never edited.
  editedAt: string | null
  createdAt: string
  updatedAt: string
  attachments?: ITaskCommentAttachment[]
  /** Emoji reactions, collapsed one entry per emoji. Absent means none. */
  reactions?: IReactionSummary[]
}

/**
 * A file attached to a task comment. Always S3-backed — unlike channel
 * attachments there is no Box-era row to discriminate against, so no `storage`
 * column. The object key itself is never sent to the client; bytes are fetched
 * by attachment id through GetTaskCommentAttachmentLink.
 */
export interface ITaskCommentAttachment {
  id: string
  commentId: string
  taskId: string
  name: string
  mimeType: string | null
  sizeBytes: number | null
  uploadedByUserId: string
  createdAt: string
}

/**
 * What the upload endpoint returns and the comment create/edit call accepts —
 * a file already in the bucket, staged by the composer until send.
 */
export interface ITaskCommentAttachmentInput {
  storageKey: string
  name: string
  mimeType: string
  sizeBytes: number
}

/** Activity rows that aren't a `tasks` column: a project-section move and a
 *  custom-field value change (see the `tasks_activity_log` column comments). */
export const TaskActivityColumn = {
  Section: 'section',
  CustomField: 'customField',
  /** The combined state of the task's linked PRs changed (see combinedPullRequestState). */
  PullRequests: 'pullRequests',
} as const

/** The rows the linked-PR baseline migration wrote: bookkeeping, never shown in the feed. */
export const PULL_REQUEST_BASELINE_ACTIVITY = 'Record Linked PR Baseline'

/** Audit log row for `tasks` table, as returned by `GET /tasks/:id/activity`. */
export interface ITaskActivityLogEntry {
  id: string
  clientName: string | null
  activity: string
  table: string
  column: string
  oldValue: string | null
  newValue: string | null
  oldLabel?: string | null
  newLabel?: string | null
  projectId?: string | null
  project?: { id: string; name: string } | null
  customFieldDefinitionId?: string | null
  customFieldDefinition?: { id: string; name: string } | null
  /** Set when an automatic rule made the change; the feed credits the rule, not a person. */
  ruleId?: string | null
  rule?: { id: string; name: string } | null
  userId: string | null
  user?: ITaskEmbeddedUser | null
  recordId: string | null
  jobId: string | null
  createdAt: string
  updatedAt: string
}

/** Response of `GET /tasks/:id/activity`. Description edits are regular rows
 *  here (column `descriptionText`), coalesced per same-user/same-day window. */
export interface ITaskActivityResponse {
  data: ITaskActivityLogEntry[]
  total: number
}

export interface IMyTasksPagination {
  page: number
  perPage: number
}

export interface IMyTasksCounts {
  // Rows matching the current filters — drives the "load more" affordance
  // against how many are currently loaded.
  active: number
  // Tasks completed today that the "Show Completed" toggle is currently
  // withholding — 0 when completed tasks are being shown, or when nothing is
  // hidden. My Tasks renders no hidden-count strip: this only feeds the empty
  // state's "You completed N task(s) today" line.
  completedHidden: number
  // How many rows the current project visibility excludes — the `projectIds`
  // selection when there is one, otherwise the viewer's hidden-from-My-Tasks
  // projects. Lets the empty state name the reason instead of reading as "you
  // have no tasks".
  hiddenByProjectFilter: number
  // Rows in this view (every page, not just the loaded ones) that carry the
  // unread dot — what "Mark all read" would clear. 0 hides the action.
  unread: number
}

/**
 * One of the viewer's own My Tasks sections. Private to them and unrelated to
 * the sections a task sits in on any task project.
 *
 * The No Section bucket has no row of its own — it is every task with no
 * placement — so it never appears here; the list renders it below these, from a
 * null `myTasksSectionId`.
 */
export interface IMyTasksSection {
  id: string
  name: string
  position: number
  // How many tasks in this section match the current filters across the WHOLE
  // result set, not just the loaded page. Rows paginate globally across
  // sections, so a section with none of its tasks loaded yet can't otherwise be
  // told apart from one the filter emptied — the list hides only the latter.
  matchingTaskCount: number
}

export interface IMyTasksResponse {
  tasks: ITask[]
  // The viewer's sections, in their order. Tasks carry `myTasksSectionId`, so
  // the list groups client-side rather than the payload nesting rows.
  sections: IMyTasksSection[]
  counts: IMyTasksCounts
  pagination: IMyTasksPagination
}

/** A task a bulk action left unchanged, and why ("Status is managed by its workflow"). */
export interface IBulkTaskSkip {
  id: string
  reason: string
}

export interface IBulkTaskResult {
  /** Ids written. Can overlap `skipped` for bulk Update, which still writes a task's unlocked fields. */
  processed: string[]
  skipped: IBulkTaskSkip[]
  success: { message: string }
}
